import { UserHelper } from '@/helpers/user.helper';
import { EmailWithTLDValidator } from '@/validators/EmailWithTLDValidator';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ErrorBusinessCode,
  PropertySale,
  SalesManagementService,
  SellingPlayerType,
  SellingPosition,
  User,
  UserRole
} from '@eemo/api-client';
import { UserSummaryDetail } from '@eemo/api-client/model/userSummaryDetail';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { NgIf } from '@angular/common';
import { InputComponent } from '../ui-forms/input/input.component';

@Component({
    selector: 'app-sale-player-association',
    templateUrl: './sale-player-association.component.html',
    styleUrls: ['./sale-player-association.component.scss'],
    standalone: true,
    imports: [NgIf, FormsModule, ReactiveFormsModule, MatProgressSpinnerModule, TranslateModule, InputComponent]
})
export class SalePlayerAssociationComponent implements OnInit, OnDestroy {
  @Input() playerType: SellingPlayerType;
  @Input() position: SellingPosition;
  @Input() sale: PropertySale;

  readonly USER_ROLES_ALLOWED = { users: [] };
  autoCompletionType;
  autoCompletedOptions: User[] | UserSummaryDetail[];
  addPlayerSub = new Subscription();
  delPlayerSub = new Subscription();
  getSaleSub = new Subscription();
  isLoading = false;
  user: User;
  titleText;
  inputLabel = 'input.email';

  form = this.fb.group({
    user: [null, EmailWithTLDValidator]
  });

  constructor(
    private fb: UntypedFormBuilder,
    private salesService: SalesManagementService,
    private userHelper: UserHelper,
    private changeDetectorRefs: ChangeDetectorRef
  ) {}

  get form_user() {
    return this.form.get('user') as FormControl<User | string>;
  }

  ngOnInit(): void {
    this.autoCompletionType = this.userHelper.isAdmin() ? 'users' : 'contacts';
    this.loadData();
  }

  loadData() {
    if (this.playerType === SellingPlayerType.NOTARY) {
      this.USER_ROLES_ALLOWED.users = [UserRole.NOTARY];
      if (this.position === SellingPosition.BUYER) {
        this.titleText = 'notaryBuyer';
        this.user = this.sale.buyerNotary as User;
      } else {
        this.titleText = 'notarySeller';
        this.user = this.sale.sellerNotary as User;
      }
    } else {
      this.USER_ROLES_ALLOWED.users = [UserRole.REAL_ESTATE];
      if (this.position === SellingPosition.BUYER) {
        this.titleText = 'realtorBuyer';
        this.user = this.sale.buyerRealtor as User;
      } else {
        this.titleText = 'realtorSeller';
        this.user = this.sale.sellerRealtor as User;
      }
    }
    this.form_user.setValue(this.user);
    if (this.user) {
      this.inputLabel = 'input.user';
    }
  }

  ngOnDestroy(): void {
    this.addPlayerSub.unsubscribe();
    this.delPlayerSub.unsubscribe();
    this.getSaleSub.unsubscribe();
  }

  onAddUser(user: User) {
    this.inputLabel = 'input.user';
    this.user = user;
    this.form_user.setValue(user);
    this.addPlayerUser(user.email);
  }

  onAddUserEmail(email: string) {
    this.user = null;
    this.addPlayerUser(email);
  }

  onDeleteUser() {
    this.inputLabel = 'input.email';
    this.removePlayerUser();
    this.user = null;
    this.form_user.setValue(null);
  }

  onValidateUserEmail() {
    this.onAddUserEmail(this.form_user.value as string);
  }

  // ---------------------------------------- //
  //                   APIS                   //
  // ---------------------------------------- //

  addPlayerUser(email: string) {
    if (!this.form.valid && !this.user) return;
    this.isLoading = true;
    this.addPlayerSub = this.salesService
      .addSalePlayer(this.sale.uid, this.position, this.playerType, email)
      .subscribe({
        next: (res: void) => {
          //reaload the sale
          this.getSaleSub = this.salesService.getSale(this.sale.uid).subscribe((s) => {
            this.sale = s;
            this.loadData();
          });
          this.isLoading = false;
        },
        error: (err: HttpErrorResponse) => {
          if (err.error && err.error.businessCode) {
            switch (err.error.businessCode) {
              case ErrorBusinessCode.EMAIL_NOT_FOUND:
                //need a set timeout otherwise control is still valid
                setTimeout(() => this.form_user.setErrors({ emailNotFound: true }));
                break;
              case ErrorBusinessCode.BAD_USER_ROLE:
                setTimeout(() => this.form_user.setErrors({ badUserRole: true }));
                break;
            }
            this.isLoading = false;
          }
        }
      });
  }

  removePlayerUser() {
    this.isLoading = true;
    this.addPlayerSub = this.salesService.deleteSalePlayer(this.sale.uid, this.position, this.playerType).subscribe({
      next: (res: void) => {
        this.isLoading = false;
      }
    });
  }
}
